import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import {
  Print as PrintIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import realDataAccountingService from '../../services/realDataAccountingService';
import { getCurrencySymbolFromSchool } from '../../utils/currencyFormatter';

const CashFlowTab = () => {
  // Redux state - get data directly from Redux like BalanceSheet
  const schooldetails = useSelector(state => state.schooldetails || []);
  const currentSchool = schooldetails.length > 0 ? schooldetails[0] : null;
  
  // Get Redux data at component level - using correct structure like BalanceSheet
  const studentsData = useSelector(state => state.students?.data || state.students || []);
  const teachersData = useSelector(state => state.teachers?.data || state.teachers || []);
  const expensesData = useSelector(state => state.expenses?.data || state.expenses || []);
  const salesData = useSelector(state => state.sales?.sales || state.sales || []);
  const assetsData = useSelector(state => state.assets?.assets || state.assets || []);
  const classData = useSelector(state => state.class?.data || state.class || []);
  
  // Create reduxData object - memoized to prevent unnecessary re-renders
  const reduxData = useMemo(() => ({
    students: Array.isArray(studentsData) ? studentsData : [],
    teachers: Array.isArray(teachersData) ? teachersData : [],
    expenses: Array.isArray(expensesData) ? expensesData : [],
    sales: Array.isArray(salesData) ? salesData : [],
    assets: Array.isArray(assetsData) ? assetsData : [],
    class: Array.isArray(classData) ? classData : []
  }), [studentsData, teachersData, expensesData, salesData, assetsData, classData]);
  
  // Get currency symbol from school details
  const currencySymbol = getCurrencySymbolFromSchool(schooldetails);
  
  // Local state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [cashFlowData, setCashFlowData] = useState(null);
  
  // Refs to prevent infinite loops
  const isCalculatingRef = useRef(false);
  const prevDataRef = useRef({ key: null });

  // Calculate date range based on period
  const dateRange = useMemo(() => {
    const today = new Date();
    let start = new Date();
    let end = new Date(today);
    end.setHours(23, 59, 59, 999);

    switch (selectedPeriod) {
      case 'current':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        break;
      case 'quarter':
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        start = quarterStart;
        start.setHours(0, 0, 0, 0);
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        start.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
    }

    return { start, end };
  }, [selectedPeriod, startDate, endDate]);

  // Load cash flow statement from real data
  const loadCashFlowStatement = useCallback(async () => {
    if (isCalculatingRef.current) return;
    if (!currentSchool?.schoolID) return;
    if (!reduxData.students.length && !reduxData.expenses.length && !reduxData.teachers.length) return;

    const dataKey = `${currentSchool.schoolID}-${dateRange.start.getTime()}-${dateRange.end.getTime()}-${reduxData.students.length}-${reduxData.expenses.length}-${reduxData.teachers.length}`;
    if (prevDataRef.current.key === dataKey) return;

    isCalculatingRef.current = true;
    prevDataRef.current.key = dataKey;
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Loading cash flow statement from real data for school:', currentSchool.schoolID);
      console.log('📊 Redux data available:', {
        students: reduxData.students?.length || 0,
        teachers: reduxData.teachers?.length || 0,
        expenses: reduxData.expenses?.length || 0,
        sales: reduxData.sales?.length || 0,
        assets: reduxData.assets?.length || 0,
        class: reduxData.class?.length || 0
      });

      // Generate cash flow statement from real data
      const data = await realDataAccountingService.generateRealCashFlowStatement(
        currentSchool.schoolID,
        dateRange.start.toISOString().split('T')[0],
        dateRange.end.toISOString().split('T')[0],
        reduxData
      );

      console.log('📊 Cash flow statement data received:', data);
      setCashFlowData(data);
    } catch (err) {
      console.error('Cash flow statement error:', err);
      const errorMessage = 'Failed to load cash flow statement: ' + (err.message || 'Unknown error');
      setError(errorMessage);
    } finally {
      setLoading(false);
      isCalculatingRef.current = false;
    }
  }, [currentSchool?.schoolID, dateRange, reduxData]);

  // Load data when component mounts or dependencies change
  useEffect(() => {
    if (currentSchool?.schoolID) {
      loadCashFlowStatement();
    }
  }, [currentSchool?.schoolID, dateRange, loadCashFlowStatement]);

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00';
    }
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  if (loading && !cashFlowData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !cashFlowData) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadCashFlowStatement}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!cashFlowData) {
    return (
      <Box>
        <Alert severity="info">No data available. Please select a period and refresh.</Alert>
        <Button variant="contained" onClick={loadCashFlowStatement} sx={{ mt: 2 }}>
          Load Data
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            onChange={handlePeriodChange}
            label="Period"
          >
            <MenuItem value="current">Current Month</MenuItem>
            <MenuItem value="quarter">Current Quarter</MenuItem>
            <MenuItem value="year">Current Year</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
        
        {selectedPeriod === 'custom' && (
          <>
            <TextField
              label="Start Date"
              type="date"
              value={startDate.toISOString().split('T')[0]}
              onChange={(e) => setStartDate(new Date(e.target.value))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate.toISOString().split('T')[0]}
              onChange={(e) => setEndDate(new Date(e.target.value))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </>
        )}
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadCashFlowStatement}
          disabled={loading}
        >
          Refresh Data
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Net Cash Change
                  </Typography>
                  <Typography variant="h4" color={cashFlowData.netCashChange >= 0 ? 'success.main' : 'error.main'}>
                    {currencySymbol}{formatCurrency(cashFlowData.netCashChange)}
                  </Typography>
                </Box>
                <AccountBalanceIcon 
                  color={cashFlowData.netCashChange >= 0 ? 'success' : 'error'} 
                  sx={{ fontSize: 40 }} 
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Operating Activities
              </Typography>
              <Typography variant="h4" color={cashFlowData.operatingTotal >= 0 ? 'success.main' : 'error.main'}>
                {currencySymbol}{formatCurrency(cashFlowData.operatingTotal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Period
              </Typography>
              <Typography variant="body1">
                {new Date(cashFlowData.period.start).toLocaleDateString()} - {new Date(cashFlowData.period.end).toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cash Flow Statement Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Cash Flow Statement
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Period: {new Date(cashFlowData.period.start).toLocaleDateString()} to {new Date(cashFlowData.period.end).toLocaleDateString()}
          </Typography>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>CASH FLOW ACTIVITIES</strong></TableCell>
                <TableCell align="right"><strong>Amount ({currencySymbol})</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Operating Activities */}
              <TableRow>
                <TableCell><strong>OPERATING ACTIVITIES</strong></TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Net Income</TableCell>
                <TableCell align="right">
                  {cashFlowData.operatingActivities.netIncome >= 0 ? '+' : ''}
                  {currencySymbol}{formatCurrency(cashFlowData.operatingActivities.netIncome)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Depreciation</TableCell>
                <TableCell align="right">
                  +{currencySymbol}{formatCurrency(cashFlowData.operatingActivities.depreciation)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Accounts Receivable Change</TableCell>
                <TableCell align="right">
                  {cashFlowData.operatingActivities.accountsReceivableChange >= 0 ? '+' : ''}
                  {currencySymbol}{formatCurrency(cashFlowData.operatingActivities.accountsReceivableChange)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Inventory Change</TableCell>
                <TableCell align="right">
                  {cashFlowData.operatingActivities.inventoryChange >= 0 ? '+' : ''}
                  {currencySymbol}{formatCurrency(cashFlowData.operatingActivities.inventoryChange)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Accounts Payable Change</TableCell>
                <TableCell align="right">
                  {cashFlowData.operatingActivities.accountsPayableChange >= 0 ? '+' : ''}
                  {currencySymbol}{formatCurrency(cashFlowData.operatingActivities.accountsPayableChange)}
                </TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Net Cash from Operating Activities</strong></TableCell>
                <TableCell align="right">
                  <strong>
                    {cashFlowData.operatingTotal >= 0 ? '+' : ''}
                    {currencySymbol}{formatCurrency(cashFlowData.operatingTotal)}
                  </strong>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={2}><Divider /></TableCell>
              </TableRow>

              {/* Investing Activities */}
              <TableRow>
                <TableCell><strong>INVESTING ACTIVITIES</strong></TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Equipment Purchase</TableCell>
                <TableCell align="right">
                  {currencySymbol}{formatCurrency(cashFlowData.investingActivities.equipmentPurchase)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Building Improvements</TableCell>
                <TableCell align="right">
                  {currencySymbol}{formatCurrency(cashFlowData.investingActivities.buildingImprovements)}
                </TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Net Cash from Investing Activities</strong></TableCell>
                <TableCell align="right">
                  <strong>
                    {currencySymbol}{formatCurrency(cashFlowData.investingTotal)}
                  </strong>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={2}><Divider /></TableCell>
              </TableRow>

              {/* Financing Activities */}
              <TableRow>
                <TableCell><strong>FINANCING ACTIVITIES</strong></TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Loan Proceeds</TableCell>
                <TableCell align="right">
                  +{currencySymbol}{formatCurrency(cashFlowData.financingActivities.loanProceeds)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Loan Payments</TableCell>
                <TableCell align="right">
                  {currencySymbol}{formatCurrency(cashFlowData.financingActivities.loanPayments)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ pl: 4 }}>Capital Contributions</TableCell>
                <TableCell align="right">
                  +{currencySymbol}{formatCurrency(cashFlowData.financingActivities.capitalContributions)}
                </TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Net Cash from Financing Activities</strong></TableCell>
                <TableCell align="right">
                  <strong>
                    {currencySymbol}{formatCurrency(cashFlowData.financingTotal)}
                  </strong>
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={2}><Divider /></TableCell>
              </TableRow>

              {/* Net Cash Change */}
              <TableRow sx={{ bgcolor: cashFlowData.netCashChange >= 0 ? 'success.light' : 'error.light' }}>
                <TableCell><strong>NET CASH CHANGE</strong></TableCell>
                <TableCell align="right">
                  <strong>
                    {cashFlowData.netCashChange >= 0 ? '+' : ''}
                    {currencySymbol}{formatCurrency(cashFlowData.netCashChange)}
                  </strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default CashFlowTab;
